/**
 * Card "SLA" da coluna direita do detalhe (v3): metas de atendimento/solução,
 * prazos formatados, chip de estado derivado dos timestamps e Pausar/Retomar.
 * As metas em minutos (attendGoalMinutes/solveGoalMinutes) já vêm resolvidas
 * pela API no TicketDetail — precedência item de catálogo > prioridade.
 */
import { useEffect, useState } from 'react';
import type { TicketDetail } from '@erp/shared';
import { CirclePause, CirclePlay } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { cn, formatDateTime } from '@/lib/utils';
import { useSlaAction } from './tickets-mutations';
import { errorMessage, isTicketReadOnly } from './ticket-format';
import { SlaChip } from './ticket-chips';
import { formatDueDistance, formatSlaGoal, slaTargetState } from './ticket-sla';

/** Relógio compartilhado do card — re-renderiza a cada 30s para o countdown. */
function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function SlaCard({ ticket }: { ticket: TicketDetail }) {
  const now = useNow();
  const slaAction = useSlaAction(ticket.ticketNumber);

  const hasSla = ticket.attendDueAt !== null || ticket.solveDueAt !== null;
  // pausar/retomar é escrita: some em fechado/cancelado e em espelhado (spec 011)
  const isClosedOrCanceled = isTicketReadOnly(ticket);
  const isCanceled = ticket.status.kind === 'canceled';
  const isPaused = ticket.slaPausedAt !== null;

  const attendState = isCanceled
    ? null
    : slaTargetState({
        dueAt: ticket.attendDueAt,
        doneAt: ticket.attendedAt,
        pausedAt: ticket.slaPausedAt,
        startAt: ticket.createdAt,
        now,
      });
  const solveState = isCanceled
    ? null
    : slaTargetState({
        dueAt: ticket.solveDueAt,
        doneAt: ticket.closedAt,
        pausedAt: ticket.slaPausedAt,
        startAt: ticket.createdAt,
        now,
      });

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">SLA</h2>
        {hasSla && !isClosedOrCanceled ? (
          <Button
            variant="outline"
            size="sm"
            disabled={slaAction.isPending}
            onClick={() => slaAction.mutate(isPaused ? 'resume' : 'pause')}
          >
            {slaAction.isPending ? (
              <Spinner className="size-4" />
            ) : isPaused ? (
              <CirclePlay />
            ) : (
              <CirclePause />
            )}
            {isPaused ? 'Retomar' : 'Pausar'}
          </Button>
        ) : null}
      </div>

      {!hasSla ? (
        <p className="text-sm text-muted-foreground">
          Sem metas de SLA — defina minutos na prioridade ou no item de catálogo.
        </p>
      ) : (
        <div className="space-y-4">
          <SlaTargetRow
            label="Atendimento"
            goalMinutes={ticket.attendGoalMinutes}
            dueAt={ticket.attendDueAt}
            doneAt={ticket.attendedAt}
            state={attendState}
            now={now}
          />
          <SlaTargetRow
            label="Solução"
            goalMinutes={ticket.solveGoalMinutes}
            dueAt={ticket.solveDueAt}
            doneAt={ticket.closedAt}
            state={solveState}
            now={now}
          />

          {isPaused ? (
            <p className="border-t pt-3 text-xs text-muted-foreground">
              Pausado desde {formatDateTime(ticket.slaPausedAt)} — os prazos serão
              recalculados na retomada.
            </p>
          ) : null}
          {ticket.slaPausedMinutes > 0 ? (
            <p className={cn('text-xs text-muted-foreground', !isPaused && 'border-t pt-3')}>
              Tempo pausado acumulado: {formatSlaGoal(ticket.slaPausedMinutes)}.
            </p>
          ) : null}
        </div>
      )}

      {slaAction.isError ? (
        <p className="mt-3 text-sm text-destructive">{errorMessage(slaAction.error)}</p>
      ) : null}
    </section>
  );
}

function SlaTargetRow({
  label,
  goalMinutes,
  dueAt,
  doneAt,
  state,
  now,
}: {
  label: string;
  goalMinutes: number | null;
  dueAt: string | Date | null;
  doneAt: string | Date | null;
  state: ReturnType<typeof slaTargetState>;
  now: Date;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{label}</span>
        {state ? <SlaChip state={state} /> : <span className="text-sm text-muted-foreground">—</span>}
      </div>
      {dueAt ? (
        <div className="space-y-0.5 text-sm">
          <p>
            <span className="text-muted-foreground">Meta:</span>{' '}
            {goalMinutes !== null ? formatSlaGoal(goalMinutes) : '—'}
            <span className="text-muted-foreground"> · Prazo:</span> {formatDateTime(dueAt)}
          </p>
          {state === 'met' && doneAt ? (
            <p className="text-xs text-muted-foreground">
              Cumprido em {formatDateTime(doneAt)}
            </p>
          ) : state === 'overdue' || state === 'due_soon' || state === 'on_track' ? (
            <p
              className={cn(
                'text-xs',
                state === 'overdue' ? 'text-destructive' : 'text-muted-foreground'
              )}
            >
              {formatDueDistance(dueAt, now)}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Sem meta definida.</p>
      )}
    </div>
  );
}
